/**
 * Parser for the `render_data` tool's result payload — see plan D2 for the
 * wire contract. The backend's ADK function tool marshals its Go struct
 * output flat (no `{"result": …}` wrapper), so this parser reads top-level
 * fields directly and must never look for a nested `.result` key.
 */

export const RENDER_DATA_TOOL_NAME = 'render_data'

/** Cards beyond this count collapse into a "View all N …" link instead of rendering. */
export const MAX_RENDERED_CARDS = 6

export interface RenderedInquiry {
  id: string
  contactId: string
  contactName: string
  stage: string
  eventType: string
  eventLocation: string
  guestCount: number
  createdAt: string
}

export interface RenderedEvent {
  id: string
  contactId: string
  contactName: string
  bookingId: string
  stage: string
  eventDate: string
  eventLocation: string
  guestCount: number
  createdAt: string
}

export interface RenderedContact {
  id: string
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
}

export interface RenderedTask {
  id: string
  title: string
  description: string
  status: string
  dueDate: string
  assigneeId: string
  assignee: string
  parentType: string
  parentId: string
  parentLabel: string
  updatedAt: string
}

interface RenderDataPayloadFields {
  notFoundIds: string[]
  totalRequested: number
  truncated: boolean
  error: string
}

export type RenderDataPayload =
  | (RenderDataPayloadFields & { entityType: 'inquiry'; entities: RenderedInquiry[] })
  | (RenderDataPayloadFields & { entityType: 'event'; entities: RenderedEvent[] })
  | (RenderDataPayloadFields & { entityType: 'contact'; entities: RenderedContact[] })
  | (RenderDataPayloadFields & { entityType: 'task'; entities: RenderedTask[] })

type EntityType = RenderDataPayload['entityType']

const ENTITY_TYPES: EntityType[] = ['inquiry', 'event', 'contact', 'task']

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function readString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function readNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && !Number.isNaN(value) ? value : fallback
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

function parseInquiry(raw: Record<string, unknown>): RenderedInquiry | null {
  const id = readString(raw.id)
  if (!id) return null
  return {
    id,
    contactId: readString(raw.contact_id),
    contactName: readString(raw.contact_name),
    stage: readString(raw.stage),
    eventType: readString(raw.event_type),
    eventLocation: readString(raw.event_location),
    guestCount: readNumber(raw.guest_count),
    createdAt: readString(raw.created_at),
  }
}

function parseEvent(raw: Record<string, unknown>): RenderedEvent | null {
  const id = readString(raw.id)
  if (!id) return null
  return {
    id,
    contactId: readString(raw.contact_id),
    contactName: readString(raw.contact_name),
    bookingId: readString(raw.booking_id),
    stage: readString(raw.stage),
    eventDate: readString(raw.event_date),
    eventLocation: readString(raw.event_location),
    guestCount: readNumber(raw.guest_count),
    createdAt: readString(raw.created_at),
  }
}

function parseContact(raw: Record<string, unknown>): RenderedContact | null {
  const id = readString(raw.id)
  if (!id) return null
  return {
    id,
    firstName: readString(raw.first_name),
    lastName: readString(raw.last_name),
    email: readString(raw.email),
    phoneNumber: readString(raw.phone_number),
  }
}

function parseTask(raw: Record<string, unknown>): RenderedTask | null {
  const id = readString(raw.id)
  if (!id) return null
  return {
    id,
    title: readString(raw.title),
    description: readString(raw.description),
    status: readString(raw.status),
    dueDate: readString(raw.due_date),
    assigneeId: readString(raw.assignee_id),
    assignee: readString(raw.assignee),
    parentType: readString(raw.parent_type),
    parentId: readString(raw.parent_id),
    parentLabel: readString(raw.parent_label),
    updatedAt: readString(raw.updated_at),
  }
}

// Each wire entity is a single-key wrapper keyed by the payload's entity_type
// (e.g. `{ "inquiry": { … } }`). A wrapper missing that key — including one
// keyed by a different entity type — yields no entity for that element.
function unwrapEntity(entityType: EntityType, raw: unknown): Record<string, unknown> | null {
  if (!isRecord(raw)) return null
  const wrapped = raw[entityType]
  return isRecord(wrapped) ? wrapped : null
}

/**
 * Parses a `render_data` tool result (`ToolExecution.resultJson`) into a
 * typed, camelCase payload, or `null` if the shape is anything other than
 * exactly what the backend contract promises. Callers treat `null` as
 * "render the ordinary tool marker instead" — never as an empty card list.
 */
export function parseRenderDataResult(resultJson: string | undefined): RenderDataPayload | null {
  if (!resultJson) return null

  let raw: unknown
  try {
    raw = JSON.parse(resultJson)
  } catch {
    return null
  }

  if (!isRecord(raw)) return null

  const entityType = raw.entity_type
  if (typeof entityType !== 'string' || !ENTITY_TYPES.includes(entityType as EntityType)) return null
  const type = entityType as EntityType

  if (!Array.isArray(raw.entities)) return null

  const fields: RenderDataPayloadFields = {
    notFoundIds: readStringArray(raw.not_found_ids),
    totalRequested: readNumber(raw.total_requested),
    truncated: raw.truncated === true,
    error: readString(raw.error),
  }

  switch (type) {
    case 'inquiry': {
      const entities = raw.entities
        .map((item) => unwrapEntity('inquiry', item))
        .filter((item): item is Record<string, unknown> => item !== null)
        .map(parseInquiry)
        .filter((item): item is RenderedInquiry => item !== null)
      if (entities.length === 0) return null
      return { entityType: 'inquiry', entities, ...fields }
    }
    case 'event': {
      const entities = raw.entities
        .map((item) => unwrapEntity('event', item))
        .filter((item): item is Record<string, unknown> => item !== null)
        .map(parseEvent)
        .filter((item): item is RenderedEvent => item !== null)
      if (entities.length === 0) return null
      return { entityType: 'event', entities, ...fields }
    }
    case 'contact': {
      const entities = raw.entities
        .map((item) => unwrapEntity('contact', item))
        .filter((item): item is Record<string, unknown> => item !== null)
        .map(parseContact)
        .filter((item): item is RenderedContact => item !== null)
      if (entities.length === 0) return null
      return { entityType: 'contact', entities, ...fields }
    }
    case 'task': {
      const entities = raw.entities
        .map((item) => unwrapEntity('task', item))
        .filter((item): item is Record<string, unknown> => item !== null)
        .map(parseTask)
        .filter((item): item is RenderedTask => item !== null)
      if (entities.length === 0) return null
      return { entityType: 'task', entities, ...fields }
    }
  }
}
